import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { LazyQueryHookOptions, useLazyQuery } from '@apollo/client';
import clsx from 'clsx';
import {
  searchGroupRatesByPropertyIdsQuery,
  SearchGroupRatesByPropertyIdsInput,
  SearchGroupRatesByPropertyIdsResponse,
  SearchGroupRatesEdge,
} from '@marriott/mi-groups-graphql';
import { Button, Types, baseVariables, InlineMessages, InlineMessagesType } from '@marriott/mi-ui-library';
import { Attachments, EventInfo } from '../../molecules';
import { OptionType } from '../../molecules/EventInfo/EventInfo.types';
import { useMediaQuery } from '../../hooks';
import {
  getSession,
  prepopulateSearchCriteria,
  getFormattedDateString,
  getQueryParams,
  isQuickGroupEligible,
  getGroupRatesSearchRequestCode,
  showBookNow,
  updateDataLayerWithRFPAttributes,
  getFormattedCalendarDate,
} from '../../utils';
import { QG_EXCLUDED_CODES, QG_EXCLUDED_CODES_ONLY_ATTENDEES } from '../../constants';
import { useLocaleStore, useRfpTrayStore, useRfpStore } from '../../store';
import { EventProfileProps, EventProfileFormData, RfpSessionData } from './EventProfile.types';
import {
  StyledCtaContainer,
  StyledEventProfile,
  StyledEventProfileContainer,
  StyledEventProfileForm,
  StyledDivider,
} from './EventProfile.styles';
import { CustomDomContentLoaded } from '../../components/CustomDomContentLoaded/CustomDomContentLoaded';
import { QuickGroupInterstitial } from './QuickGroupInterstitial';
import { RfpPageIdentifier } from '../RfpHeader/RfpHeader.types';
import { EventProfileSkeletonLoader } from './EventProfileSkeletonLoader/EventProfileSkeletonLoader';

export const EventProfileConfig = {
  emptyLabel: 'EventProfile',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/rfp/eventprofile`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const EventProfile: FC<{ model: EventProfileProps }> = (props: any) => {
  const {
    eventProfileLabel,
    eventName,
    dates,
    flexibleDates,
    eventType,
    otherEventType,
    guestRooms,
    attendees,
    attachments,
    continueCtaLabel,
    continueCtaUrl,
    continueCtaLocation,
    continueCtaDescription,
    quickGroupInterstitial,
    propertiesNotFoundError,
  } = props.model;
  const defaultFlexibleDatesOption = flexibleDates.options.filter((option: OptionType) => option.selected === true);

  const router = useRouter();

  const isTablet = useMediaQuery(baseVariables.mediaQuery.md);

  const { locale } = useLocaleStore();
  const { properties, setPropertyIds } = useRfpTrayStore();
  const { eventProfileData, setEventProfileData, setRfpSessionData, pageVisited, setPageVisited } = useRfpStore();

  const [loader, setLoader] = useState(true);
  const [flexibleDateOptions, setFlexibleDateOptions] = useState(flexibleDates.options);
  const [eventTypeOptions, setEventTypeOptions] = useState(eventType.options);
  const [isOtherEvent, setIsOtherEvent] = useState<boolean>(false);
  const [error, setError] = useState(false);

  const [propertyId, setPropertyId] = useState('');
  const [isHwsFlow, setIsHwsFlow] = useState<boolean>(false);
  const [showInterstitial, setShowInterstitial] = useState<boolean>(false);
  const [interstitialData, setInterstitialData] = useState<SearchGroupRatesEdge>();

  const [getGroupRatesByPropertyIds, { loading }] = useLazyQuery<{
    searchGroupRatesByPropertyIds: SearchGroupRatesByPropertyIdsResponse;
  }>(searchGroupRatesByPropertyIdsQuery);

  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    clearErrors,
    watch,
    reset,
    formState: { errors },
  } = useForm<EventProfileFormData>({
    defaultValues: {
      eventName: '',
      dates: '',
      startDate: '',
      endDate: '',
      flexibleDates: {
        label: defaultFlexibleDatesOption[0]?.label,
        code: defaultFlexibleDatesOption[0]?.code,
        selected: defaultFlexibleDatesOption[0]?.selected,
      },
      eventType: {
        label: '',
        code: '',
        selected: false,
      },
      otherEventType: '',
      guestRooms: '',
      attendees: '',
      files: [],
    },
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    const subscription = watch(() => {
      const data = getValues();
      setEventProfileData({
        ...data,
        startDate: getFormattedCalendarDate(data.startDate, 'hyphenatedDateWithMonthNoAndYear'),
        endDate: getFormattedCalendarDate(data.endDate, 'hyphenatedDateWithMonthNoAndYear'),
      });
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  useEffect(() => {
    const queryParams = getQueryParams(window.location.search);
    setPropertyId(queryParams?.['marshaCode']);
    if (pageVisited[RfpPageIdentifier.EVENT_PROFILE]) {
      prepopulateEventProfileInfo();
      setLoader(false);
    } else {
      fetchSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSession = async () => {
    try {
      const { sessionData } = (await getSession()) as RfpSessionData;
      const groupSearchData = sessionData?.cacheData?.data;

      const marshaCodesFromQueryparams = getQueryParams(window.location.search)?.['marshaCode'];
      const queryParamsPropertyIds = marshaCodesFromQueryparams?.toUpperCase().split(',') || [];

      if (groupSearchData) {
        const ariesGroupSearch = groupSearchData.AriesGroupSearch;
        const propertyIds = ariesGroupSearch?.rfpShortlistedMarshaCodes
          ? Object.keys(ariesGroupSearch?.rfpShortlistedMarshaCodes)
          : [];
        const hasPropertyIdUpdates =
          queryParamsPropertyIds.length === 1 &&
          (propertyIds.length > 1 || (propertyIds.length === 1 && propertyIds[0] !== queryParamsPropertyIds[0]));

        if (!ariesGroupSearch || !propertyIds.length || hasPropertyIdUpdates) {
          setIsHwsFlow(true);
          setPropertyIds(queryParamsPropertyIds);
          // @ts-expect-error AriesGroupSearch requires all mandatory fields; only assigning rfpShortlistedMarshaCodes here.
          groupSearchData.AriesGroupSearch = {
            rfpShortlistedMarshaCodes: { [queryParamsPropertyIds[0]]: '' },
          };
        } else {
          setPropertyIds(propertyIds);
        }
        prepopulateSearchCriteria(
          groupSearchData,
          flexibleDateOptions,
          eventTypeOptions,
          setFlexibleDateOptions,
          setEventTypeOptions,
          setValue
        );

        setRfpSessionData(groupSearchData);
        updateDataLayerWithRFPAttributes(groupSearchData);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const prepopulateEventProfileInfo = () => {
    const flexibleDateOptions = flexibleDates.options.map((option: OptionType) => {
      if (option.label === eventProfileData.flexibleDates.label) {
        return { ...option, selected: true };
      }
      return option;
    });
    setFlexibleDateOptions(flexibleDateOptions);

    const eventTypeOptions = eventType.options.map((option: OptionType) => {
      if (option.label.toLowerCase() === eventProfileData.eventType.label.toLowerCase()) {
        return { ...option, selected: true };
      }
      return option;
    });
    setEventTypeOptions(eventTypeOptions);
    if (eventProfileData.otherEventType) setIsOtherEvent(true);

    reset(eventProfileData);
  };

  const saveEventProfileCallback = (data: EventProfileFormData) => {
    if (!properties || !properties.length) {
      setError(true);
      window.scrollTo(0, 0);
      return;
    }

    setEventProfileData({
      ...data,
      startDate: getFormattedCalendarDate(data.startDate, 'hyphenatedDateWithMonthNoAndYear'),
      endDate: getFormattedCalendarDate(data.endDate, 'hyphenatedDateWithMonthNoAndYear'),
    });

    const isQuickGroup = isQuickGroupEligible(
      locale,
      +getValues('guestRooms'),
      +getValues('attendees'),
      getFormattedDateString(data.startDate),
      getFormattedDateString(data.endDate)
    );

    if (isHwsFlow && isQuickGroup && !getValues('files').length) {
      fetchGroupRatesByPropertyId();
    } else {
      setPageVisited(RfpPageIdentifier.EVENT_PROFILE, true);
      router.push(continueCtaUrl);
    }
  };

  const fetchGroupRatesByPropertyId = () => {
    const numberOfRooms = +getValues('guestRooms');
    const numberOfAttendees = +getValues('attendees');

    const searchGroupRatesByPropertyIdsInput: SearchGroupRatesByPropertyIdsInput = {
      ids: propertyId,
      startDate: getFormattedDateString(getValues('startDate')),
      endDate: getFormattedDateString(getValues('endDate')),
      numberOfRooms,
      numberOfAttendees,
      options: {
        numberInParty: 1,
        ...(numberOfAttendees ? { largestEventSpace: numberOfAttendees * 10 } : { numberOfGuestRooms: numberOfRooms }),
      },
      excludedGroupLevelCodes: numberOfAttendees ? QG_EXCLUDED_CODES_ONLY_ATTENDEES : QG_EXCLUDED_CODES,
      groupId: `M0${Date.now()}`,
      requestCode: getGroupRatesSearchRequestCode(numberOfRooms, numberOfAttendees),
    };

    const uxlOptions: LazyQueryHookOptions = {
      variables: { search: searchGroupRatesByPropertyIdsInput },
      context: {
        headers: {
          'accept-language': locale,
        },
      },
      fetchPolicy: 'network-only',
      onCompleted: data => {
        const groupRatesData = data?.searchGroupRatesByPropertyIds.edges?.[0];

        if (
          groupRatesData.groupRate &&
          showBookNow(
            groupRatesData.groupRate,
            numberOfRooms,
            numberOfAttendees,
            locale,
            groupRatesData?.node?.basicInformation?.marshaBrandCode
          )
        ) {
          setInterstitialData(groupRatesData);
          setShowInterstitial(true);
        } else {
          setPageVisited(RfpPageIdentifier.EVENT_PROFILE, true);
          router.push(continueCtaUrl);
        }
      },
    };

    getGroupRatesByPropertyIds(uxlOptions);
  };

  if (loader) {
    return <EventProfileSkeletonLoader />;
  }

  return (
    <EditableComponent config={EventProfileConfig} {...props}>
      <CustomDomContentLoaded>
        <StyledEventProfileContainer
          data-testid="eventprofile"
          data-component-name="o-groups-eventprofile"
          className="fullbleed m-container-fullbleed"
        >
          <StyledEventProfile className="container pt-0">
            {error ? (
              <div className="mb-4">
                <InlineMessages type={InlineMessagesType.Error} title={propertiesNotFoundError} severity="1" />
              </div>
            ) : null}
            <form onSubmit={handleSubmit(saveEventProfileCallback)}>
              {isTablet !== null ? (
                <StyledEventProfileForm>
                  <EventInfo
                    labels={{
                      eventProfileLabel,
                      eventName,
                      dates,
                      flexibleDates,
                      eventType,
                      otherEventType,
                      guestRooms,
                      attendees,
                    }}
                    flexibleDateOptions={flexibleDateOptions}
                    eventTypeOptions={eventTypeOptions}
                    control={control}
                    setValue={setValue}
                    getValues={getValues}
                    errors={errors}
                    clearErrors={clearErrors}
                    isOtherEvent={isOtherEvent}
                    setIsOtherEvent={setIsOtherEvent}
                  />
                  <StyledDivider />
                  <Attachments labels={attachments} control={control} />
                </StyledEventProfileForm>
              ) : null}
              <StyledCtaContainer>
                <Button
                  type={Types.ButtonTypeVariation.Submit}
                  className={clsx('m-button-primary m-button-m continue-cta', loading && 'skeleton-loader')}
                  isDisabled={loading}
                  custom_click_track_value={`${continueCtaLocation} | ${continueCtaDescription} | internal`}
                >
                  {continueCtaLabel}
                </Button>
                {showInterstitial && interstitialData ? (
                  <QuickGroupInterstitial
                    labels={quickGroupInterstitial}
                    data={interstitialData}
                    show={showInterstitial}
                    onCloseModal={() => setShowInterstitial(false)}
                  />
                ) : null}
              </StyledCtaContainer>
            </form>
          </StyledEventProfile>
        </StyledEventProfileContainer>
      </CustomDomContentLoaded>
    </EditableComponent>
  );
};
